import type { ThemePresetConfig } from './types';

// Font stacks
const fontSans =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const fontMono = '"JetBrains Mono", "Fira Code", "Consolas", monospace';
const fontSerif = '"Playfair Display", Georgia, "Times New Roman", serif';

// Spacing scale (1rem = 16px base)
const spacingScale = '0.25rem';

/**
 * Modern Minimal - Default theme
 * Clean, elegant, minimal with subtle depth
 */
export const modernMinimal: ThemePresetConfig = {
  name: 'modern-minimal',
  label: 'Modern Minimal',
  description: 'Clean and elegant with subtle depth',
  light: {
    '--bg': '#fafafa',
    '--fg': '#171717',
    '--muted': '#f5f5f5',
    '--muted-fg': '#737373',
    '--card': '#ffffff',
    '--card-fg': '#171717',
    '--border': '#e8e8e8',
    '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)',
    '--accent': '#171717',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#404040',
    '--link': '#2563eb',
    '--link-hover': '#1d4ed8',
    '--focus': '#3b82f6',
    '--radius': '0.5rem',
    '--font-sans': fontSans,
    '--font-mono': fontMono,
    '--font-serif': fontSerif,
    '--heading-weight': '700',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#111111',
    '--fg': '#fafafa',
    '--muted': '#1c1c1e',
    '--muted-fg': '#a3a3a3',
    '--card': '#1c1c1e',
    '--card-fg': '#fafafa',
    '--border': '#2c2c2e',
    '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)',
    '--accent': '#fafafa',
    '--accent-fg': '#171717',
    '--accent-hover': '#e5e5e5',
    '--link': '#60a5fa',
    '--link-hover': '#93c5fd',
    '--focus': '#60a5fa',
    '--radius': '0.5rem',
    '--font-sans': fontSans,
    '--font-mono': fontMono,
    '--font-serif': fontSerif,
    '--heading-weight': '700',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Neo-Brutalism
 * Bold borders, high contrast, crisp offset shadows
 */
export const neoBrutalism: ThemePresetConfig = {
  name: 'neo-brutalism',
  label: 'Neo-Brutalism',
  description: 'Bold borders, high contrast, offset shadows',
  light: {
    '--bg': '#f8f5f2',
    '--fg': '#1a1a1a',
    '--muted': '#e8e4e0',
    '--muted-fg': '#666666',
    '--card': '#ffffff',
    '--card-fg': '#1a1a1a',
    '--border': '#1a1a1a',
    '--shadow': '4px 4px 0 0 #1a1a1a',
    '--accent': '#ff6b35',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#e55a2b',
    '--link': '#0066ff',
    '--link-hover': '#0052cc',
    '--focus': '#ff6b35',
    '--radius': '0.125rem',
    '--font-sans': '"Space Grotesk", "Archivo", "IBM Plex Sans", sans-serif',
    '--font-mono': '"IBM Plex Mono", "JetBrains Mono", monospace',
    '--font-serif': '"Fraunces", serif',
    '--heading-weight': '700',
    '--body-weight': '500',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#1a1a1a',
    '--fg': '#f8f5f2',
    '--muted': '#2a2a2a',
    '--muted-fg': '#a0a0a0',
    '--card': '#252525',
    '--card-fg': '#f8f5f2',
    '--border': '#f8f5f2',
    '--shadow': '4px 4px 0 0 #f8f5f2',
    '--accent': '#ff6b35',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#ff8555',
    '--link': '#66b3ff',
    '--link-hover': '#99ccff',
    '--focus': '#ff6b35',
    '--radius': '0.125rem',
    '--font-sans': '"Space Grotesk", "Archivo", "IBM Plex Sans", sans-serif',
    '--font-mono': '"IBM Plex Mono", "JetBrains Mono", monospace',
    '--font-serif': '"Fraunces", serif',
    '--heading-weight': '700',
    '--body-weight': '500',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Minimalism
 * Ultra-clean, minimal borders, maximum whitespace
 */
export const minimalism: ThemePresetConfig = {
  name: 'minimalism',
  label: 'Minimalism',
  description: 'Ultra-clean with maximum whitespace',
  light: {
    '--bg': '#ffffff',
    '--fg': '#1a1a1a',
    '--muted': '#f5f5f5',
    '--muted-fg': '#666666',
    '--card': '#ffffff',
    '--card-fg': '#1a1a1a',
    '--border': '#e0e0e0',
    '--shadow': 'none',
    '--accent': '#1a1a1a',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#333333',
    '--link': '#1a1a1a',
    '--link-hover': '#666666',
    '--focus': '#1a1a1a',
    '--radius': '0',
    '--font-sans': fontSans,
    '--font-mono': fontMono,
    '--font-serif': fontSerif,
    '--heading-weight': '300',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#0a0a0a',
    '--fg': '#fafafa',
    '--muted': '#1a1a1a',
    '--muted-fg': '#888888',
    '--card': '#0a0a0a',
    '--card-fg': '#fafafa',
    '--border': '#2a2a2a',
    '--shadow': 'none',
    '--accent': '#fafafa',
    '--accent-fg': '#0a0a0a',
    '--accent-hover': '#e0e0e0',
    '--link': '#fafafa',
    '--link-hover': '#aaaaaa',
    '--focus': '#fafafa',
    '--radius': '0',
    '--font-sans': fontSans,
    '--font-mono': fontMono,
    '--font-serif': fontSerif,
    '--heading-weight': '300',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Flat Design
 * No shadows, solid colors, simple shapes
 */
export const flatDesign: ThemePresetConfig = {
  name: 'flat-design',
  label: 'Flat Design',
  description: 'No shadows, solid colors, simple shapes',
  light: {
    '--bg': '#ffffff',
    '--fg': '#2c3e50',
    '--muted': '#f8f9fa',
    '--muted-fg': '#7f8c8d',
    '--card': '#f8f9fa',
    '--card-fg': '#2c3e50',
    '--border': '#e0e0e0',
    '--shadow': 'none',
    '--accent': '#3498db',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#2980b9',
    '--link': '#2980b9',
    '--link-hover': '#1abc9c',
    '--focus': '#3498db',
    '--radius': '0.25rem',
    '--font-sans': '"Inter", "Source Sans 3", "IBM Plex Sans", "Noto Sans", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Manrope", "Plus Jakarta Sans", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#2c3e50',
    '--fg': '#ecf0f1',
    '--muted': '#34495e',
    '--muted-fg': '#95a5a6',
    '--card': '#34495e',
    '--card-fg': '#ecf0f1',
    '--border': '#465669',
    '--shadow': 'none',
    '--accent': '#3498db',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#5dade2',
    '--link': '#5dade2',
    '--link-hover': '#1abc9c',
    '--focus': '#3498db',
    '--radius': '0.25rem',
    '--font-sans': '"Inter", "Source Sans 3", "IBM Plex Sans", "Noto Sans", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Manrope", "Plus Jakarta Sans", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Flat 2.0
 * Flat with subtle depth and long shadows
 */
export const flat20: ThemePresetConfig = {
  name: 'flat-2-0',
  label: 'Flat 2.0',
  description: 'Flat design with subtle depth and long shadows',
  light: {
    '--bg': '#f8f9fa',
    '--fg': '#2c3e50',
    '--muted': '#e9ecef',
    '--muted-fg': '#6c757d',
    '--card': '#ffffff',
    '--card-fg': '#2c3e50',
    '--border': '#dee2e6',
    '--shadow': '0 8px 32px rgba(0, 0, 0, 0.08)',
    '--accent': '#e74c3c',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#c0392b',
    '--link': '#3498db',
    '--link-hover': '#2980b9',
    '--focus': '#e74c3c',
    '--radius': '0.5rem',
    '--font-sans': '"Poppins", "Montserrat", "DM Sans", "Rubik", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Inter", sans-serif',
    '--heading-weight': '800',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#1a1d29',
    '--fg': '#f8f9fa',
    '--muted': '#252a3a',
    '--muted-fg': '#8e97ae',
    '--card': '#252a3a',
    '--card-fg': '#f8f9fa',
    '--border': '#3a4055',
    '--shadow': '0 8px 32px rgba(0, 0, 0, 0.4)',
    '--accent': '#ff6b6b',
    '--accent-fg': '#1a1d29',
    '--accent-hover': '#ff8e8e',
    '--link': '#5dade2',
    '--link-hover': '#85c1e9',
    '--focus': '#ff6b6b',
    '--radius': '0.5rem',
    '--font-sans': '"Poppins", "Montserrat", "DM Sans", "Rubik", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Inter", sans-serif',
    '--heading-weight': '800',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Neomorphism
 * Soft shadows, extruded look with refined depth
 */
export const neomorphism: ThemePresetConfig = {
  name: 'neomorphism',
  label: 'Neomorphism',
  description: 'Soft shadows with extruded look and tactile depth',
  light: {
    '--bg': '#e6e9f0',
    '--fg': '#4a5568',
    '--muted': '#dbe0ea',
    '--muted-fg': '#6b7b8c',
    '--card': '#e6e9f0',
    '--card-fg': '#4a5568',
    '--border': 'transparent',
    '--shadow': '8px 8px 16px rgba(163,177,198,0.6), -8px -8px 16px rgba(255,255,255,0.7)',
    '--accent': '#6c5ce7',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#5b4cdb',
    '--link': '#6c5ce7',
    '--link-hover': '#5b4cdb',
    '--focus': '#6c5ce7',
    '--radius': '1rem',
    '--font-sans': '"Nunito Sans", "Rubik", "Quicksand", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Inter", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '500',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#1e2128',
    '--fg': '#e2e8f0',
    '--muted': '#2a2e36',
    '--muted-fg': '#8b95a8',
    '--card': '#1e2128',
    '--card-fg': '#e2e8f0',
    '--border': 'transparent',
    '--shadow': '10px 10px 20px rgba(0,0,0,0.5), -10px -10px 20px rgba(255,255,255,0.05)',
    '--accent': '#a29bfe',
    '--accent-fg': '#0f0f15',
    '--accent-hover': '#b8b3fe',
    '--link': '#a29bfe',
    '--link-hover': '#b8b3fe',
    '--focus': '#a29bfe',
    '--radius': '1rem',
    '--font-sans': '"Nunito Sans", "Rubik", "Quicksand", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Inter", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '500',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Glassmorphism
 * Translucent backgrounds, blur effects
 * Reference: gm dark 1-4, gm light 1-3
 */
export const glassmorphism: ThemePresetConfig = {
  name: 'glassmorphism',
  label: 'Glassmorphism',
  description: 'Translucent backgrounds with blur effects',
  light: {
    '--bg': 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e0e7ff 100%)',
    '--fg': '#0f172a',
    '--muted': 'rgba(255, 255, 255, 0.6)',
    '--muted-fg': '#475569',
    '--card': 'rgba(255, 255, 255, 0.35)',
    '--card-fg': '#0f172a',
    '--border': 'rgba(255, 255, 255, 0.4)',
    '--shadow': '0 8px 32px 0 rgba(31, 38, 135, 0.1), 0 2px 8px 0 rgba(31, 38, 135, 0.05)',
    '--accent': '#0ea5e9',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#0284c7',
    '--link': '#0284c7',
    '--link-hover': '#0369a1',
    '--focus': '#0ea5e9',
    '--radius': '1rem',
    '--font-sans': '"Inter", "SF Pro Display", "Plus Jakarta Sans", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Space Grotesk", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    '--fg': '#f8fafc',
    '--muted': 'rgba(255, 255, 255, 0.08)',
    '--muted-fg': '#94a3b8',
    '--card': 'rgba(30, 41, 59, 0.4)',
    '--card-fg': '#f8fafc',
    '--border': 'rgba(255, 255, 255, 0.12)',
    '--shadow': '0 8px 32px 0 rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    '--accent': '#38bdf8',
    '--accent-fg': '#0f172a',
    '--accent-hover': '#7dd3fc',
    '--link': '#38bdf8',
    '--link-hover': '#7dd3fc',
    '--focus': '#38bdf8',
    '--radius': '1rem',
    '--font-sans': '"Inter", "SF Pro Display", "Plus Jakarta Sans", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Space Grotesk", sans-serif',
    '--heading-weight': '600',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Gradient/Aurora
 * Multi-layered animated gradients with vibrant aurora colors
 */
export const gradientAurora: ThemePresetConfig = {
  name: 'gradient-aurora',
  label: 'Gradient/Aurora',
  description: 'Vibrant aurora borealis with animated gradient layers',
  light: {
    '--bg':
      'linear-gradient(135deg, #f0fdf4 0%, #fdf2f8 25%, #fffbeb 50%, #eff6ff 75%, #f5f3ff 100%)',
    '--fg': '#0f172a',
    '--muted': 'rgba(255, 255, 255, 0.5)',
    '--muted-fg': '#475569',
    '--card': 'rgba(255, 255, 255, 0.6)',
    '--card-fg': '#0f172a',
    '--border': 'rgba(255, 255, 255, 0.5)',
    '--shadow': '0 8px 32px rgba(0, 0, 0, 0.08)',
    '--accent': '#ec4899',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#db2777',
    '--link': '#0891b2',
    '--link-hover': '#06b6d4',
    '--focus': '#ec4899',
    '--radius': '0.75rem',
    '--font-sans': '"Inter", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Fraunces", serif',
    '--heading-weight': '500',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
    '--fg': '#f8fafc',
    '--muted': 'rgba(15, 23, 42, 0.6)',
    '--muted-fg': '#94a3b8',
    '--card': 'rgba(15, 23, 42, 0.5)',
    '--card-fg': '#f8fafc',
    '--border': 'rgba(255, 255, 255, 0.1)',
    '--shadow': '0 8px 32px rgba(0, 0, 0, 0.4)',
    '--accent': '#34d399',
    '--accent-fg': '#020617',
    '--accent-hover': '#6ee7b7',
    '--link': '#22d3ee',
    '--link-hover': '#67e8f9',
    '--focus': '#34d399',
    '--radius': '0.75rem',
    '--font-sans': '"Inter", "Manrope", sans-serif',
    '--font-mono': fontMono,
    '--font-serif': '"Fraunces", serif',
    '--heading-weight': '500',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * 8 Bit
 * Pixel art style, sharp edges, retro gaming aesthetic
 */
export const eightBit: ThemePresetConfig = {
  name: '8-bit',
  label: '8 Bit',
  description: 'Pixel art style with sharp edges and retro gaming feel',
  light: {
    '--bg': '#f5f5f0',
    '--fg': '#1a1a1a',
    '--muted': '#e8e8e0',
    '--muted-fg': '#666666',
    '--card': '#ffffff',
    '--card-fg': '#1a1a1a',
    '--border': '#1a1a1a',
    '--shadow': '4px 4px 0 0 #1a1a1a',
    '--accent': '#1a1a1a',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#333333',
    '--link': '#1a1a1a',
    '--link-hover': '#333333',
    '--focus': '#1a1a1a',
    '--radius': '0',
    '--font-sans': '"Jersey 10", monospace',
    '--font-mono': '"Jersey 10", monospace',
    '--font-serif': '"Jersey 10", monospace',
    '--heading-weight': '400',
    '--body-weight': '400',
    '--spacing-scale': '4px',
  },
  dark: {
    '--bg': '#0a0a0a',
    '--fg': '#f0f0e8',
    '--muted': '#1a1a18',
    '--muted-fg': '#888880',
    '--card': '#151513',
    '--card-fg': '#f0f0e8',
    '--border': '#f0f0e8',
    '--shadow': '4px 4px 0 0 #f0f0e8',
    '--accent': '#f0f0e8',
    '--accent-fg': '#0a0a0a',
    '--accent-hover': '#d0d0c8',
    '--link': '#f0f0e8',
    '--link-hover': '#d0d0c8',
    '--focus': '#f0f0e8',
    '--radius': '0',
    '--font-sans': '"Jersey 10", monospace',
    '--font-mono': '"Jersey 10", monospace',
    '--font-serif': '"Jersey 10", monospace',
    '--heading-weight': '400',
    '--body-weight': '400',
    '--spacing-scale': '4px',
  },
};

/**
 * Vector LED
 * Dot matrix LED display style
 * Reference: vector led.png, vector led 2.png - Dot matrix LED display
 */
export const vectorLED: ThemePresetConfig = {
  name: 'vector-led',
  label: 'Vector LED',
  description: 'Dot matrix LED display with pixel-style text',
  light: {
    '--bg': '#e8e8e8',
    '--fg': '#1a1a1a',
    '--muted': '#d0d0d0',
    '--muted-fg': '#666666',
    '--card': '#f0f0f0',
    '--card-fg': '#1a1a1a',
    '--border': '#ffb000',
    '--shadow': '0 0 8px rgba(255, 176, 0, 0.4)',
    '--accent': '#ffb000',
    '--accent-fg': '#1a1a1a',
    '--accent-hover': '#ffc833',
    '--link': '#00d4ff',
    '--link-hover': '#33e0ff',
    '--focus': '#ffb000',
    '--radius': '0.5rem',
    '--font-sans': '"Doto", monospace',
    '--font-mono': '"Doto", monospace',
    '--font-serif': '"Doto", monospace',
    '--heading-weight': '700',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#0a0a0c',
    '--fg': '#e8e8e8',
    '--muted': '#151518',
    '--muted-fg': '#555560',
    '--card': '#111114',
    '--card-fg': '#e8e8e8',
    '--border': '#ffb000',
    '--shadow': '0 0 10px rgba(255, 176, 0, 0.6), 0 0 20px rgba(255, 176, 0, 0.3)',
    '--accent': '#ffb000',
    '--accent-fg': '#0a0a0c',
    '--accent-hover': '#ffcc33',
    '--link': '#00d4ff',
    '--link-hover': '#66e5ff',
    '--focus': '#ffb000',
    '--radius': '0.5rem',
    '--font-sans': '"Doto", monospace',
    '--font-mono': '"Doto", monospace',
    '--font-serif': '"Doto", monospace',
    '--heading-weight': '600',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Retro Terminal Console
 * Monospace fonts, phosphor glow, cursor blink
 * Reference: retro terminal.png - Classic green phosphor CRT
 */
export const retroTerminal: ThemePresetConfig = {
  name: 'retro-terminal',
  label: 'Retro Terminal',
  description: 'Classic terminal console with phosphor glow and monospace fonts',
  light: {
    '--bg': '#f5f5f0',
    '--fg': '#2a2a2a',
    '--muted': '#e8e8e0',
    '--muted-fg': '#666666',
    '--card': '#fafaf5',
    '--card-fg': '#2a2a2a',
    '--border': '#333333',
    '--shadow': 'none',
    '--accent': '#2a2a2a',
    '--accent-fg': '#f5f5f0',
    '--accent-hover': '#4a4a4a',
    '--link': '#006600',
    '--link-hover': '#009900',
    '--focus': '#006600',
    '--radius': '0',
    '--font-sans': '"VT323", monospace',
    '--font-mono': '"VT323", monospace',
    '--font-serif': '"VT323", monospace',
    '--heading-weight': '400',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#0a0a08',
    '--fg': '#33ff33',
    '--muted': '#0d120d',
    '--muted-fg': '#1a5c1a',
    '--card': '#0c100c',
    '--card-fg': '#33ff33',
    '--border': '#33ff33',
    '--shadow': '0 0 10px rgba(51, 255, 51, 0.3), 0 0 20px rgba(51, 255, 51, 0.1)',
    '--accent': '#33ff33',
    '--accent-fg': '#0a0a08',
    '--accent-hover': '#66ff66',
    '--link': '#33ccff',
    '--link-hover': '#66ddff',
    '--focus': '#33ff33',
    '--radius': '0',
    '--font-sans': '"VT323", monospace',
    '--font-mono': '"VT323", monospace',
    '--font-serif': '"VT323", monospace',
    '--heading-weight': '400',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

/**
 * Dot Matrix
 * Halftone patterns, monochrome, retro print aesthetic
 */
export const dotMatrix: ThemePresetConfig = {
  name: 'dot-matrix',
  label: 'Dot Matrix',
  description: 'Halftone patterns with monochrome retro print aesthetic',
  light: {
    '--bg': '#ffffff',
    '--fg': '#000000',
    '--muted': '#f5f5f5',
    '--muted-fg': '#333333',
    '--card': '#ffffff',
    '--card-fg': '#000000',
    '--border': '#000000',
    '--shadow': 'none',
    '--accent': '#000000',
    '--accent-fg': '#ffffff',
    '--accent-hover': '#333333',
    '--link': '#000000',
    '--link-hover': '#555555',
    '--focus': '#000000',
    '--radius': '0',
    '--font-sans': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--font-mono': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--font-serif': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--heading-weight': '700',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
  dark: {
    '--bg': '#000000',
    '--fg': '#ffffff',
    '--muted': '#1a1a1a',
    '--muted-fg': '#cccccc',
    '--card': '#000000',
    '--card-fg': '#ffffff',
    '--border': '#ffffff',
    '--shadow': 'none',
    '--accent': '#ffffff',
    '--accent-fg': '#000000',
    '--accent-hover': '#cccccc',
    '--link': '#ffffff',
    '--link-hover': '#aaaaaa',
    '--focus': '#ffffff',
    '--radius': '0',
    '--font-sans': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--font-mono': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--font-serif': '"JetBrains Mono", "IBM Plex Mono", "Courier Prime", monospace',
    '--heading-weight': '700',
    '--body-weight': '400',
    '--spacing-scale': spacingScale,
  },
};

// Export all presets
export const themePresets: Record<string, ThemePresetConfig> = {
  'modern-minimal': modernMinimal,
  'neo-brutalism': neoBrutalism,
  minimalism: minimalism,
  'flat-design': flatDesign,
  'flat-2-0': flat20,
  neomorphism: neomorphism,
  glassmorphism: glassmorphism,
  'gradient-aurora': gradientAurora,
  '8-bit': eightBit,
  'vector-led': vectorLED,
  'retro-terminal': retroTerminal,
  'dot-matrix': dotMatrix,
};

// Default preset
export const defaultPreset: ThemePresetConfig = modernMinimal;
